/**
 * Step2「字段配置」—— 指标区域：区块标题、内联新增自定义指标、指标表。
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Button, Input, Select, Table, Text } from 'tea-component';

import {
  buildEmptyCustomRowEdit,
  isMeasInlineAddDraftValid,
} from '../../../utils/fieldConfigRowUtils';
import FieldConfigBatchSelectCheckbox from './FieldConfigBatchSelectCheckbox';
import { FieldConfigRequiredLabel } from './FieldConfigRequiredLabel';
import type { IMeasureSectionProps } from '../types';

/** Step2 指标区域 UI */
const MeasureSection: React.FC<IMeasureSectionProps> = ({
  measRows,
  measColumns,
  measInlineOpen,
  changeMeasInlineOpen,
  measAddDraft,
  changeMeasAddDraft,
  measTypeOptions,
  measFormatOptions,
  measGranularityOptions,
  onConfirmMeasInline,
  allMeasSourceColumns,
  fieldsValue,
  changeFieldsValue,
  secError = false,
}) => {
  const { formatMessage } = useIntl();

  /** 指标表无数据时 `topTip` 空态 */
  const measEmptyTip =
    measRows.length === 0
      ? formatMessage({ id: 'dbl_model_agent_step2_empty_meas' })
      : null;

  return (
    <section
      className={classnames('dbl-field-config-sec dbl-field-config-sec--meas', {
        'dbl-field-config-sec--error': secError,
      })}
    >
      <div className="dbl-field-config-sec-header">
        <Text className="dbl-field-config-sec-title">
          {formatMessage({ id: 'dbl_model_agent_step2_sec_measures' })}
        </Text>
        <Text className="dbl-field-config-sec-count">
          {formatMessage(
            { id: 'dbl_model_agent_summary_count_unit' },
            { n: measRows.length },
          )}
        </Text>
        <div className="dbl-field-config-sec-header-add-group">
          <FieldConfigBatchSelectCheckbox
            variant="toolbar"
            batchKeys={allMeasSourceColumns}
            fieldsValue={fieldsValue}
            changeFieldsValue={changeFieldsValue}
            formatMessage={formatMessage}
          />
          <Button
            type="weak"
            className="dbl-field-config-add-field-btn dbl-field-config-add-field-btn--meas"
            onClick={() => {
              changeMeasInlineOpen((open) => {
                if (open) {
                  return false;
                }
                changeMeasAddDraft(buildEmptyCustomRowEdit('metric'));
                return true;
              });
            }}
          >
            {formatMessage({ id: 'dbl_model_agent_step2_add_measure' })}
          </Button>
        </div>
      </div>
      {measInlineOpen ? (
        <div className="dbl-field-config-inline-add dbl-field-config-inline-add--meas">
          <Text
            className="dbl-field-config-inline-add__title"
          >
            {formatMessage({ id: 'dbl_model_agent_step2_inline_meas_heading' })}
          </Text>
          <div className="dbl-field-config-inline-add__row">
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_display' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={measAddDraft.displayName}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, displayName: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_meas_display',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_name_zh' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={measAddDraft.nameZh}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, nameZh: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_name_zh',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_name_en' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={measAddDraft.nameEn}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, nameEn: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_name_en',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field dbl-field-config-inline-add__field--narrow">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_data_type' })}
              </FieldConfigRequiredLabel>
              <Select
                size="m"
                appearance="button"
                options={measTypeOptions}
                value={measAddDraft.dataType}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, dataType: String(v) }))
                }
              />
            </div>
          </div>
          <div className="dbl-field-config-inline-add__row">
            <div className="dbl-field-config-inline-add__field dbl-field-config-inline-add__field--grow">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_sql' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={measAddDraft.sql}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, sql: v }))
                }
                placeholder={formatMessage(
                  {
                    id: 'dbl_model_agent_step2_inline_sql_placeholder_meas',
                  },
                  { CUBE: '{CUBE}' },
                )}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_desc' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={measAddDraft.description}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, description: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_description',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field dbl-field-config-inline-add__field--grow">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_common_query' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={measAddDraft.commonQuery}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, commonQuery: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_common_query',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field dbl-field-config-inline-add__field--narrow">
              <Text className="dbl-field-config-inline-add__label">
                {formatMessage({ id: 'dbl_model_agent_step2_col_format' })}
              </Text>
              <Select
                size="m"
                appearance="button"
                options={measFormatOptions}
                value={measAddDraft.format}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, format: String(v) }))
                }
              />
            </div>
            <div className="dbl-field-config-inline-add__field dbl-field-config-inline-add__field--narrow">
              <Text className="dbl-field-config-inline-add__label">
                {formatMessage({ id: 'dbl_model_agent_step2_col_granularity' })}
              </Text>
              <Select
                size="m"
                appearance="button"
                options={measGranularityOptions}
                value={measAddDraft.granularity}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, granularity: String(v) }))
                }
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <Text className="dbl-field-config-inline-add__label">
                {formatMessage({ id: 'dbl_model_agent_step2_col_filters' })}
              </Text>
              <Input
                size="full"
                value={measAddDraft.filters}
                onChange={(v) =>
                  changeMeasAddDraft((d) => ({ ...d, filters: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_filters',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__actions">
              <Button
                type="primary"
                disabled={!isMeasInlineAddDraftValid(measAddDraft)}
                onClick={onConfirmMeasInline}
              >
                {formatMessage({ id: 'dbl_model_agent_step2_modal_confirm' })}
              </Button>
              <Button type="weak" onClick={() => changeMeasInlineOpen(false)}>
                {formatMessage({ id: 'dbl_model_agent_step2_modal_cancel' })}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="dbl-field-config-table-wrap">
        <Table
          bordered
          records={measRows}
          columns={measColumns}
          recordKey={(c) => c.source_column}
          topTip={measEmptyTip}
          addons={[
            Table.addons.scrollable({
              maxHeight: 360,
              minWidth: 2310,
            }),
          ]}
        />
      </div>
    </section>
  );
};

export default MeasureSection;
